/**
 * Invoice Requests API Client
 *
 * Frontend API wrapper for organization invoice request management.
 * Extends BaseApiClient for authentication, error handling, and type-safe requests.
 *
 * Provides two sets of methods:
 * - Rich methods with response transform (getOrgRequests, createRequest) used by pages.
 * - Simple methods (getOrgInvoiceRequests, submitInvoiceRequest, cancelInvoiceRequest)
 *   used by the Zustand store slice.
 */

// GW-6533: Validate orgId at API client boundary so frontend regressions
// throw a distinct InvalidOrgIdError before the network call leaves the
// browser, instead of producing a noisy 4xx after a round-trip.
import { assertOrgId } from '@/types/orgId';

import { BaseApiClient } from './baseApiClient';

import type { CreateInvoiceRequestData, InvoiceRequest, InvoiceRequestStatus } from '@gw/ui/types';

// ============================================================================
// Response Types (camelCase from backend)
// ============================================================================

interface InvoiceRequestResponse {
  id: string;
  organizationId: string;
  requestedBy: string;
  tokenAmount: number;
  pricePerToken: number;
  totalAmount: number;
  status: InvoiceRequestStatus;
  companyName: string;
  billingEmail: string;
  billingAddress: string;
  notes?: string;
  adminNotes?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
  processedBy?: string;
  stripeInvoiceId?: string;
  paidAt?: string;
}

interface OrgRequestsResponse {
  invoiceRequests: InvoiceRequestResponse[];
  total: number;
}

// ============================================================================
// Transformed Response Types (camelCase for frontend)
// ============================================================================

interface OrgRequestsResult {
  requests: InvoiceRequest[];
}

// ============================================================================
// Transform Utilities
// ============================================================================

function transformResponseToInvoiceRequest(response: InvoiceRequestResponse): InvoiceRequest {
  return {
    id: response.id,
    organizationId: response.organizationId,
    requestedBy: response.requestedBy,
    tokenAmount: response.tokenAmount,
    pricePerToken: response.pricePerToken,
    totalAmount: response.totalAmount,
    status: response.status,
    companyName: response.companyName,
    billingEmail: response.billingEmail,
    billingAddress: response.billingAddress,
    notes: response.notes,
    adminNotes: response.adminNotes,
    rejectionReason: response.rejectionReason,
    createdAt: response.createdAt,
    updatedAt: response.updatedAt,
    processedAt: response.processedAt,
    processedBy: response.processedBy,
    stripeInvoiceId: response.stripeInvoiceId,
    paidAt: response.paidAt,
  };
}

function transformCreateRequestPayload(data: CreateInvoiceRequestData): Record<string, unknown> {
  return {
    tokenAmount: data.tokenAmount,
    companyName: data.companyName,
    billingEmail: data.billingEmail,
    billingAddress: data.billingAddress,
    ...(data.notes !== undefined && { notes: data.notes }),
  };
}

// ============================================================================
// Constants
// ============================================================================

/**
 * GW-6395: Cap the invoice-requests fetch to prevent unbounded payload growth.
 * The backend supports a `limit` query parameter. Most organizations will have
 * far fewer than this number of invoice requests, but this prevents Sentry
 * large-payload warnings as the dataset grows over time.
 */
const INVOICE_REQUESTS_FETCH_LIMIT = 50;

// ============================================================================
// API Client Class
// ============================================================================

class InvoiceRequestsApiClient extends BaseApiClient {
  // --------------------------------------------------------------------------
  // Rich API Methods (with response transform)
  // --------------------------------------------------------------------------

  /**
   * Get invoice requests for an organization (wrapped result)
   * @param orgId Organization ID
   * @returns Wrapped result with list of invoice requests
   */
  async getOrgRequests(orgId: string): Promise<OrgRequestsResult> {
    assertOrgId(orgId);
    const query = this.buildQueryString({ limit: INVOICE_REQUESTS_FETCH_LIMIT });
    const response = await this.get<OrgRequestsResponse>(
      `/organizations/${orgId}/invoice-requests${query}`
    );

    return {
      requests: (response.invoiceRequests ?? []).map(transformResponseToInvoiceRequest),
    };
  }

  /**
   * Create a new invoice request for an organization
   * @param orgId Organization ID
   * @param data Invoice request data
   * @returns Created invoice request
   */
  async createRequest(orgId: string, data: CreateInvoiceRequestData): Promise<InvoiceRequest> {
    assertOrgId(orgId);
    const payload = transformCreateRequestPayload(data);
    const response = await this.post<InvoiceRequestResponse>(
      `/organizations/${orgId}/invoice-requests`,
      payload
    );

    return transformResponseToInvoiceRequest(response);
  }

  // --------------------------------------------------------------------------
  // Store Slice API Methods (used by orgInvoiceRequestsSlice)
  // --------------------------------------------------------------------------

  /**
   * Get invoice requests for an organization (flat array)
   *
   * GET /organizations/:orgId/invoice-requests?limit={INVOICE_REQUESTS_FETCH_LIMIT}
   *
   * @param orgId Organization ID
   * @returns Flat array of invoice requests (capped at INVOICE_REQUESTS_FETCH_LIMIT)
   */
  async getOrgInvoiceRequests(orgId: string): Promise<InvoiceRequest[]> {
    assertOrgId(orgId);
    const query = this.buildQueryString({ limit: INVOICE_REQUESTS_FETCH_LIMIT });
    const response = await this.get<OrgRequestsResponse>(
      `/organizations/${orgId}/invoice-requests${query}`
    );
    return (response.invoiceRequests ?? []).map(transformResponseToInvoiceRequest);
  }

  /**
   * Submit a new invoice request
   *
   * POST /organizations/:orgId/invoice-requests
   *
   * @param orgId Organization ID
   * @param data Invoice request data
   * @returns Created invoice request
   */
  async submitInvoiceRequest(
    orgId: string,
    data: CreateInvoiceRequestData
  ): Promise<InvoiceRequest> {
    assertOrgId(orgId);
    const payload = transformCreateRequestPayload(data);
    const response = await this.post<InvoiceRequestResponse>(
      `/organizations/${orgId}/invoice-requests`,
      payload
    );
    return transformResponseToInvoiceRequest(response);
  }

  /**
   * Cancel an invoice request
   *
   * PUT /organizations/:orgId/invoice-requests/:requestId/cancel
   *
   * @param orgId Organization ID
   * @param requestId Invoice request ID
   */
  async cancelInvoiceRequest(orgId: string, requestId: string): Promise<void> {
    assertOrgId(orgId);
    await this.put<void>(`/organizations/${orgId}/invoice-requests/${requestId}/cancel`, {});
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

export const invoiceRequestsApi = new InvoiceRequestsApiClient();
